import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ChevronRight, Info, Loader2, Send, Smile, Sparkles, X,
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

// Sohbet **yalnızca Herkül şemasındadır**; diğer kabuklar bu bileşeni hiç
// çizmez. İki kabuğu vardır ve **ikisi aynı anda çizilmez**: geniş ekranda
// sağdaki 320px ray, 1400px altında (rayın sığmadığı genişlikte) tam ekran
// bir sayfa (`sheet`). Kabuk seçimi çağıran tarafta yapılır; iki örnek
// birden bağlansaydı akış iki kez yoklanırdı.
//
// Akış sunucudan **yoklamayla** alınır. WebSocket kullanılmadı: uygulama tek
// uvicorn worker ile çalışır (bkz. CLAUDE.md "Dikkat edilecekler") ve kalıcı
// bağlantı başına bir soket tutmak, oyuncu sayısı arttığında sağlayıcı
// webhook'larıyla aynı süreci paylaşan sunucuyu yorar.
//
// İmleç **sayaçtır, zaman değildir**: `created_at` MySQL'de saniye
// çözünürlüğünde saklandığı için aynı saniyeye düşen ikinci mesaj zaman
// imleciyle yoklandığında hiç gelmezdi (bkz. backend/services/chat.py).
const POLL_MS = 5000;

// Sunucudaki `MAX_LENGTH` ile aynı kalmalıdır; yanıt bu değeri döndürdüğü
// için buradaki sabit yalnızca ilk boyamada kullanılır.
const FALLBACK_MAX = 160;

// Kullanıcı adından sabit bir renk türetilir: sohbette kimin yazdığı ada
// bakmadan da ayırt edilebilmelidir. Renkler şemanın paletinden seçildi.
const NAME_COLORS = [
  '#e4bb6c', '#4eb8c0', '#00ff85', '#ff9500', '#bf5af2', '#00d4ff', '#f0723d',
];

const nameColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) % 997;
  }
  return NAME_COLORS[hash % NAME_COLORS.length];
};

const timeLabel = (iso) => {
  if (!iso) return '';
  const date = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
};

const HerkulChat = ({ sheet = false, onCollapse, onRequireLogin }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [maxLength, setMaxLength] = useState(FALLBACK_MAX);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  // İmleç ref'te tutulur: yoklama döngüsü her turda yeniden kurulmasın diye
  // state'e bağlanmaz.
  const cursor = useRef(0);
  const listRef = useRef(null);
  const pinnedToBottom = useRef(true);

  // Yeni mesaj geldiğinde yalnızca oyuncu **zaten en alttaysa** kaydırılır;
  // yukarı çıkıp geçmişi okuyan birini aşağı zıplatmak sohbeti okunmaz yapar.
  const rememberScroll = useCallback(() => {
    const node = listRef.current;
    if (!node) return;
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
    pinnedToBottom.current = distance < 60;
  }, []);

  useEffect(() => {
    const node = listRef.current;
    if (!node || !pinnedToBottom.current) return;
    node.scrollTop = node.scrollHeight;
  }, [messages]);

  // Akışı yoklar. `after=0` ilk yüklemedir (son N mesaj), sonrası artımlıdır.
  const poll = useCallback(async () => {
    try {
      const params = cursor.current ? { after: cursor.current } : {};
      const { data } = await api.get('/chat/messages', { params });
      const incoming = Array.isArray(data?.items) ? data.items : [];
      if (data?.max_length) setMaxLength(data.max_length);
      if (incoming.length) {
        cursor.current = incoming[incoming.length - 1].seq || cursor.current;
        setMessages((prev) => {
          // Gönderen istemci mesajını iyimser olarak eklemiş olabilir; aynı
          // kimlik iki kez çizilmemelidir.
          const seen = new Set(prev.map((item) => item.id));
          const fresh = incoming.filter((item) => !seen.has(item.id));
          if (!fresh.length) return prev;
          // Ray sınırlı yükseklikte; sınırsız birikim belleği şişirir.
          return [...prev, ...fresh].slice(-120);
        });
      }
      setLoaded(true);
    } catch {
      // Yoklama hatası sessiz geçilir: sohbet yan bir özelliktir, geçici bir
      // ağ hatası için oyuncuya hata basmak sayfayı gürültülü yapar.
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    let timer = null;
    const tick = async () => {
      if (!alive) return;
      await poll();
      if (alive) timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [poll]);

  const remaining = maxLength - draft.length;

  const submit = async (event) => {
    event.preventDefault();
    if (!user) { onRequireLogin?.(); return; }
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError('');
    try {
      const { data } = await api.post('/chat/messages', { body });
      setDraft('');
      if (data?.id) {
        cursor.current = Math.max(cursor.current, data.seq || 0);
        setMessages((prev) => (
          prev.some((item) => item.id === data.id) ? prev : [...prev, data].slice(-120)
        ));
        pinnedToBottom.current = true;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Mesaj gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const rows = useMemo(() => messages.map((item) => ({
    ...item,
    color: nameColor(item.username || ''),
    time: timeLabel(item.created_at),
    mine: !!user && item.username === user.username,
  })), [messages, user]);

  return (
    <aside className={`herkul-chat ${sheet ? 'is-sheet' : ''}`} aria-label="Sohbet">
      <header className="herkul-chat-head">
        <span className="herkul-chat-mark"><Sparkles size={15} /></span>
        <div className="herkul-chat-title">
          <strong>Sohbet</strong>
          <span className="herkul-chat-status">
            <i className={user ? 'is-on' : ''} />
            {user ? 'Bağlısınız' : 'Lütfen giriş yapın'}
          </span>
        </div>
        <button type="button" className="herkul-chat-tool" aria-label="Sohbet kuralları">
          <Info size={15} />
        </button>
        {onCollapse && (
          <button
            type="button"
            className="herkul-chat-tool"
            onClick={onCollapse}
            aria-label={sheet ? 'Sohbeti kapat' : 'Sohbeti gizle'}
          >
            {/* Tam ekran sayfada ok "rayı kenara it" anlamına gelmez; orada
                düğme sayfayı kapatır ve işareti çarpıdır. */}
            {sheet ? <X size={18} /> : <ChevronRight size={16} />}
          </button>
        )}
      </header>

      <div className="herkul-chat-list" ref={listRef} onScroll={rememberScroll}>
        {!loaded && (
          <div className="herkul-chat-empty"><Loader2 size={16} className="herkul-spin" /></div>
        )}
        {loaded && rows.length === 0 && (
          <p className="herkul-chat-empty">Henüz mesaj yok. İlk mesajı sen at!</p>
        )}
        {rows.map((item) => (
          <div key={item.id} className={`herkul-chat-row ${item.mine ? 'is-mine' : ''}`}>
            <span className="herkul-chat-name" style={{ color: item.color }}>
              {item.username}
            </span>
            <span className="herkul-chat-body">{item.body}</span>
            {item.time && <span className="herkul-chat-time">{item.time}</span>}
          </div>
        ))}
      </div>

      {error && <p className="herkul-chat-error">{error}</p>}

      <form className="herkul-chat-compose" onSubmit={submit}>
        <span className="herkul-chat-emoji" aria-hidden="true"><Smile size={16} /></span>
        <input
          className="herkul-chat-input"
          value={draft}
          maxLength={maxLength}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={() => { if (!user) onRequireLogin?.(); }}
          placeholder={user ? 'Mesaj yaz... (Enter ile gönder)' : 'Yazmak için giriş yapın'}
          disabled={!user || sending}
          aria-label="Sohbet mesajı"
        />
        <button
          type="submit"
          className="herkul-chat-send"
          disabled={!user || sending || !draft.trim()}
          aria-label="Gönder"
        >
          {sending ? <Loader2 size={15} className="herkul-spin" /> : <Send size={15} />}
        </button>
      </form>

      <div className="herkul-chat-foot">
        <span>{draft.length}/{maxLength}</span>
        {!user ? (
          <button type="button" className="herkul-chat-login" onClick={onRequireLogin}>
            • Lütfen giriş yapın
          </button>
        ) : (
          <span className={remaining < 20 ? 'is-low' : ''}>
            {remaining < 20 ? `${remaining} karakter kaldı` : ''}
          </span>
        )}
      </div>
    </aside>
  );
};

export default HerkulChat;
